import './signOutPage.scss';

import { ThemeProvider } from '@mui/material/styles';
import { AuthType } from '@thesavingsgroup/auth/AuthType';
import { PHONE_NUMBER_PATTERN } from '@thesavingsgroup/regex';
import { toDigits } from '@thesavingsgroup/utils/toDigits';
import { ReactNode, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import heroReferenceMessage from '../../assets/hero-reference-message.svg';
import { Alert } from '../../components/Alert/Alert';
import { Button } from '../../components/Button/Button';
import { Header } from '../../components/Header/Header';
import { NumberInput } from '../../components/NumberInput/NumberInput';
import useApiStore from '../../store/apiStore/useApiStore';
import { DeliverPinLocation } from '../../store/pinStore/enums/DeliverPinLocation';
import usePinStore from '../../store/pinStore/usePinStore';
import useSignOutStore from '../../store/signOutStore/useSignOutStore';
import useThemeStore from '../../store/themeStore/useThemeStore';
import { SessionCheckTrigger } from '../../store/userStore/enums/SessionCheckTrigger';
import useUserStore from '../../store/userStore/useUserStore';
import { locator } from '../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { isLoading } = useApiStore();
  const { deliverPin, deliverPinFailedMessage } = usePinStore();
  const { t } = useTranslation();
  const {
    bootstrapSession,
    computed: { hasCurrentUser },
  } = useUserStore();
  const {
    clearSignOutRedirect,
    redirectingToSignOut,
    signOutMessage,
    signOut,
  } = useSignOutStore();

  const form = useForm({
    mode: 'onTouched',
    criteriaMode: 'firstError',
    defaultValues: {
      primaryPhoneNumberValue: '',
    },
    delayError: 200,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const phoneNumberValidation = {
    setValueAs: (value: string): string => toDigits(value),
    required: t('validation.phone.required'),
    pattern: {
      value: PHONE_NUMBER_PATTERN,
      message: t('validation.phone.invalid'),
    },
  };

  useEffect((): void => {
    // this route has no SessionGuard, so a direct load checks the session here
    void bootstrapSession(SessionCheckTrigger.SIGN_OUT_PAGE);
  }, [bootstrapSession]);

  useEffect((): void => {
    if (hasCurrentUser) {
      signOut();
    }
  }, [hasCurrentUser]);

  useEffect((): void => {
    // the sign-out has reached this page, so SessionGuard can stop redirecting here
    if (redirectingToSignOut) {
      clearSignOutRedirect();
    }
  }, [redirectingToSignOut]);

  const handleRedirect = (): void => {
    usePinStore.setState({ deliverPinFailedMessage: null });
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { primaryPhoneNumberValue } = values;

    useSignOutStore.setState({ signOutMessage: null });

    await deliverPin({
      deliverPinLocation: DeliverPinLocation.SIGN_IN,
      phone: primaryPhoneNumberValue,
      purpose: AuthType.ACCOUNT_SIGN_IN_USING_PIN,
    });

    /*
      DeliverPin sets the deliverPinSuccess in state, the
      .getState() call forces a re-render to pick up the new value
    */
    if (usePinStore.getState().deliverPinSuccess) {
      navigate('/sign-in/pin-verify');
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="page sign-out-page">
        <Header
          trailing={
            <Link
              data-testid={locator.pages.signOut.header.signupLink}
              to="/sign-up"
              onClick={handleRedirect}
            >
              {t('SignOutPage.signup')}
            </Link>
          }
          showLogo
          splitLayout
        />

        <main>
          <figure>
            <img
              alt={t('imageAlts.heroReference')}
              src={heroReferenceMessage}
            />
          </figure>
          <form
            data-testid={locator.pages.signOut.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.signOut.form.h1}>
                {t('SignOutPage.title')}
              </h1>

              {(deliverPinFailedMessage || signOutMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={locator.pages.signOut.alert.error}
                    severity="error"
                  >
                    <Trans
                      components={{
                        signup: (
                          <Link
                            data-testid={locator.pages.signOut.alert.signupLink}
                            to="/sign-up"
                            onClick={handleRedirect}
                          />
                        ),
                      }}
                    >
                      {deliverPinFailedMessage || signOutMessage}
                    </Trans>
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <NumberInput
                  {...register(
                    'primaryPhoneNumberValue',
                    phoneNumberValidation,
                  )}
                  autoComplete="tel-national"
                  data-testid={locator.pages.signOut.form.phoneInput}
                  error={errors.primaryPhoneNumberValue}
                  errorTestId={locator.pages.signOut.form.phoneInputError}
                  label={t('SignOutPage.phoneNumber')}
                  phone
                />
              </div>

              <Button
                data-testid={locator.pages.signOut.form.submitButton}
                loader={isLoading}
                loaderPosition="start"
                fullWidth
                submit
              >
                {t('SignOutPage.continue')}
              </Button>

              <p className="form-sign-up">
                {t('SignOutPage.noAccount')}&nbsp;
                <Link
                  data-testid={locator.pages.signOut.form.signupLink}
                  to="/sign-up"
                  onClick={handleRedirect}
                >
                  {t('SignOutPage.signup')}
                </Link>
              </p>
            </div>
            <footer data-testid={locator.pages.signOut.footer.root}>
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>
    </ThemeProvider>
  );
}

Component.displayName = 'SignOutPage';
